'use strict';

import React from 'react';
import styled from 'styled-components';

import Section from '@latitude/section';
import { Box, Flex } from '@latitude/box';
import {
  COLOR,
  BREAKPOINT,
  MARGIN,
  FONT_FAMILY,
  ALIGN
} from '@latitude/core/utils/constants';
import Text from '@latitude/text/Text';
import SvgInline from '@latitude/svg-inline';

import './_contacting-latitude.css';

const content1 = (
  <div class="featureSliderText">
    <p>Received a suspicious email or text message?</p>
    <ul>
      <li>
        If you believe you've received a Gem branded scam, please forward
        the email or a screenshot of the text message to{' '}
        <a href="mailto:scams@latitudefinancial.com">
          scams@latitudefinancial.com
        </a>{' '}
        and then delete the message.
      </li>
      <li>
        Please note that while we monitor this inbox, we aren't able to respond
        to individual emails. You'll receive an automated response to let you
        know that we are looking into it.
      </li>
      <li>
        If you've responded to or actioned a suspicious email or message, it's
        possible your security has been compromised. Please{' '}
        <a
          href="https://gemfinance.zendesk.com/hc/en-nz/articles/360010403418-Contact-us"
          target="_blank"
        >
          contact us
        </a>{' '}
        immediately.
      </li>
    </ul>
  </div>
);
const content2 = (
  <div class="featureSliderText">
    <p>Seeing some suspicious activity on your account?</p>
    <p>
      If you believe there's fraudulent activity on your account,{' '}
      <a
        href="https://gemfinance.zendesk.com/hc/en-nz/articles/360010403418-Contact-us"
        target="_blank"
      >
        contact us
      </a>{' '}
      immediately so we can make sure your account is secure.
    </p>
    <p>
      If you're concerned about a transaction or charge appearing on your
      account that may not be yours, you can raise a{' '}
      <a
        href="https://gemfinance.zendesk.com/hc/en-nz/articles/360008641478-Dispute-a-Transaction"
        target="_blank"
      >
        dispute
      </a>{' '}
      and we will investigate it.
    </p>
  </div>
);
const content3 = (
  <div class="featureSliderText">
    <p>Have a lost, stolen, or damaged card?</p>
    <p>Don't panic, you can cancel the card and we'll send you a new one.</p>
    <p>
      Just let us know using the mobile app, Latitude Service Centre or via our
      online form.
    </p>
    <p>
      For more information on how to report a lost, stolen or damaged card,
      please visit{' '}
      <a
        href="https://gemfinance.zendesk.com/hc/en-nz/articles/360007997758-Report-a-Lost-Stolen-or-Damaged-Card"
        target="_blank"
      >
        here
      </a>
      .
    </p>
  </div>
);

const ContactingLatitude = ({ anchor, heading }) => {
  return (
    <Section className="security-contacting-latitude">
      <SectionHeading id={anchor}>{heading}</SectionHeading>
      <SubHeading>
        To protect your personal information, this is who you need to contact if
        you're concerned about your online security.
      </SubHeading>
      <Outer isResponsive id="products" css="max-width:1110px;">
        <Group>
          <GroupItem>
            <Card>
              <ItemIcon>
                <SvgInline name="icon-mail" />
              </ItemIcon>
              <ItemTitle>Report a Scam</ItemTitle>
              {content1}
            </Card>
          </GroupItem>
          <GroupItem>
            <Card>
              <ItemIcon>
                <SvgInline name="icon-padlock-confetti" />
              </ItemIcon>
              <ItemTitle>Report a Suspicious Transaction</ItemTitle>
              {content2}
            </Card>
          </GroupItem>
          <GroupItem>
            <Card>
              <ItemIcon>
                <SvgInline name="icon-cards-secure-confetti" />
              </ItemIcon>
              <ItemTitle>Report a Lost or Stolen Card</ItemTitle>
              {content3}
            </Card>
          </GroupItem>
        </Group>
      </Outer>
    </Section>
  );
};

const SectionHeading = styled.h2`
  margin: 40pt 0 15pt 0;
  color: #000;
  text-align: center;
  font-size: 24px;
  font-family: ${FONT_FAMILY.TITLE};
  line-height: 32px;
  font-weight: 600;
  @media (min-width: ${BREAKPOINT.LG}) {
    font-size: 32px;
    line-height: 40px;
    padding-bottom: 0;
  }
`;

const SubHeading = styled(Text)`
  text-align: ${ALIGN.CENTER};
  color: ${COLOR.GREY75};
  margin: 0 auto ${MARGIN.M24};
  @media (min-width: ${BREAKPOINT.SM}) {
    margin-bottom: ${MARGIN.M32};
  }
`;

const Outer = styled(Box)`
  position: relative;
  margin: 0 auto 42px;
  @media (min-width: ${BREAKPOINT.XL}) {
    margin-bottom: 72px;
  }
`;

const ItemIcon = styled.div`
  text-align: center;
  content-align: center;
`;

const ItemTitle = styled.h3`
  color: ${COLOR.BLACK};
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  margin-top: 0;
`;

const Group = styled.div`
  z-index: 10;
  @media (min-width: ${BREAKPOINT.LG}) {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;
const GroupItem = styled.div`
  margin-bottom: 30px;
  @media (min-width: ${BREAKPOINT.LG}) {
    flex-basis: calc(33.3% - 15px);
  }
`;

const Card = styled.div`
  padding: 24px;
  background-color: #fff;
  box-shadow: 0 0 7px 0 rgba(0, 0, 0, 0.07);
  max-width: 365px;
  margin: 0 auto;
  text-align: centre;
  @media (min-width: ${BREAKPOINT.LG}) {
    height: 100%;
  }
`;

export default ContactingLatitude;
